import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { FaTimes } from "react-icons/fa";

let _setOpen = null;
let _setContent = null;
let _currentRoute = null;

export function openDrawer(node, route = null) {
  _setContent && _setContent(node);
  _setOpen && _setOpen(true);
  _currentRoute = route;
}
export function closeDrawer() {
  _setOpen && _setOpen(false);
  _currentRoute = null;
}

export default function RightDrawer() {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [width, setWidth] = useState(420);
  const [isResizing, setIsResizing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [visible, setVisible] = useState(false);
  const drawerRef = useRef(null);
  const location = useLocation();

  _setOpen = useCallback((val) => {
    if (val) {
      setClosing(false);
      setOpen(true);
      // Small delay so the DOM mounts before animation starts
      requestAnimationFrame(() => setVisible(true));
    } else {
      setClosing(true);
      setVisible(false);
      setTimeout(() => {
        setOpen(false);
        setClosing(false);
      }, 350);
    }
  }, []);
  _setContent = setContent;

  useEffect(() => {
    if (open && _currentRoute && _currentRoute !== location.pathname) {
      _setOpen(false);
      setContent(null);
      _currentRoute = null;
    }
  }, [location.pathname, open, _setOpen]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      const minWidth = 300;
      const maxWidth = window.innerWidth * 0.8;
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (!open && !closing) return null;

  return (
    <>
      {/* Animated backdrop overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-sm ${
          visible && !closing ? "animate-backdrop-in" : "animate-backdrop-out"
        }`}
        onClick={() => {
          closeDrawer();
        }}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`fixed inset-y-0 right-0 z-50 flex flex-col
          bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl
          border-l border-white/20 dark:border-slate-600/30
          ${isResizing ? "" : visible && !closing ? "animate-drawer-slide-in" : "animate-drawer-slide-out"}
        `}
        style={{
          width: `${width}px`,
          boxShadow: visible
            ? '-8px 0 40px rgba(0,0,0,0.12), -2px 0 8px rgba(0,0,0,0.06)'
            : 'none',
        }}
      >
        {/* Decorative gradient top bar */}
        <div
          className="h-1 w-full bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500 bg-[length:200%_auto] animate-gradient-x"
        />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200/80 dark:border-slate-700/80">
          <div
            className={`font-semibold text-slate-900 dark:text-slate-100 text-base ${
              visible && !closing ? "animate-drawer-header-in opacity-0" : ""
            }`}
          >
            Details
          </div>
          <button
            className="group relative w-8 h-8 rounded-lg flex items-center justify-center
              text-slate-400 dark:text-slate-500
              hover:text-white hover:bg-red-500
              transition-all duration-300 ease-out
              hover:shadow-lg hover:shadow-red-500/25 hover:scale-110
              active:scale-95"
            onClick={closeDrawer}
          >
            <FaTimes className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90" />
          </button>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-auto p-5 drawer-content-area">
          {typeof content === 'function' ? content() : content}
        </div>

        {/* Resize handle with glow effect */}
        <div
          className="absolute left-0 top-0 w-1.5 h-full group cursor-col-resize"
          onMouseDown={() => setIsResizing(true)}
        >
          <div className="w-full h-full bg-slate-200/60 dark:bg-slate-600/60 transition-all duration-300 group-hover:bg-emerald-400 group-hover:w-1 group-hover:shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
        </div>
      </div>
    </>
  );
}
